/**
 * ============================================================================
 * Upload Summary Component
 * ============================================================================
 * Displays current database statistics for uploaded reference files
 * ============================================================================
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { trpc } from "@/lib/trpc";
import { Database, Loader2, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const TABLE_LABELS: Record<string, { name: string; description: string }> = {
  fe_master: {
    name: "FE Master",
    description: "Emission factors database",
  },
  mapping: {
    name: "Mapping",
    description: "Activity to emission factor mappings",
  },
  jobs: {
    name: "Jobs",
    description: "Sectors and activities classification",
  },
  geo_units: {
    name: "Geo Units",
    description: "Countries and regions",
  },
};

export default function UploadSummary() {
  const { data: stats, isLoading, refetch, isRefetching } = trpc.upload.stats.useQuery(undefined, {
    refetchOnWindowFocus: false,
  });
  
  const clearTableMutation = trpc.upload.clearTable.useMutation({
    onSuccess: (data) => {
      toast.success(data.message);
      refetch();
    },
    onError: (error) => {
      toast.error(`Failed to clear table: ${error.message}`);
    },
  });
  
  const handleClearTable = (tableName: 'fe_master' | 'mapping' | 'jobs' | 'geo_units') => {
    clearTableMutation.mutate({ tableName });
  };

  const getTotalRows = () => {
    if (!stats) return 0;
    return stats.reduce((sum, stat) => sum + stat.count, 0);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Database className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Database Summary</CardTitle>
              <CardDescription>Current row counts for uploaded reference files</CardDescription>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isRefetching}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Table</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Last Upload</TableHead>
                  <TableHead className="text-right">Rows</TableHead>
                  <TableHead className="text-right">Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats?.map((stat) => {
                  const label = TABLE_LABELS[stat.table];
                  const isEmpty = stat.count === 0;
                  const lastUpload = stat.lastUpload;
                  
                  return (
                    <TableRow key={stat.table}>
                      <TableCell className="font-medium">{label?.name || stat.table}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {label?.description || ""}
                      </TableCell>
                      <TableCell className="text-sm">
                        {lastUpload ? (
                          <div className="flex flex-col">
                            <span className="font-medium">{lastUpload.file_name}</span>
                            <span className="text-xs text-muted-foreground">
                              {new Date(lastUpload.uploaded_at).toLocaleString()}
                            </span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {stat.count.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        {isEmpty ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                            Empty
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            ✓ Loaded
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={isEmpty || clearTableMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Clear {label?.name}?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete all {stat.count.toLocaleString()} rows from the {label?.name} table.
                                This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleClearTable(stat.table as any)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Clear Table
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {/* Total Summary */}
            <div className="mt-4 pt-4 border-t flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Total rows across all tables
              </div>
              <div className="text-2xl font-bold text-primary">
                {getTotalRows().toLocaleString()}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

